import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import type { TagOption } from 'components/shared/Tags/model';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { MultiValueContainer } from 'components/shared/underlyings/selector/multi/multi-value-container';
import { MenuList } from 'components/shared/underlyings/selector/result/menu-list';
import { NoOptionsMessage } from 'components/shared/underlyings/selector/result/no-option-message';
import { Option } from 'components/shared/underlyings/selector/result/option';
import { SearchIcon } from 'components/shared/underlyings/selector/search-icon';
import { useUnderlyingsBasket } from 'hooks/underlyings/use-underlyings-basket';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';
import type { Underlying } from 'models/underlying';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { GroupBase, SelectComponentsConfig } from 'react-select';

type Props = {
  id: string;
  placeholder?: string;
  underlyings: readonly Underlying[];
  onChange: (underlyings: readonly Underlying[]) => void;
  defaultUnderlyingCodeType: UnderlyingCodeType;
  hasErrors?: boolean;
  tabIndex?: number;
  components?: SelectComponentsConfig<TagOption<Underlying>, true, GroupBase<TagOption<Underlying>>>;
  keepOnBlur?: boolean;
  isFakeValidateButtonHidden?: boolean;
  isFocused?: boolean;
};

export function MultiUnderlyingSelector({
  id,
  placeholder,
  onChange,
  keepOnBlur = false,
  underlyings,
  hasErrors,
  defaultUnderlyingCodeType,
  tabIndex,
  getOptionsFromPastedLabels,
  search,
  components,
  isFakeValidateButtonHidden = false,
  isFocused,
}: Props & UnderlyingSearch) {
  const { updateUnderlyings, tags, newValidTags, onTagsChanged, onTagHighlightToggled, onTagRemoved } =
    useUnderlyingsTags({
      underlyings,
      onChange,
      isMulti: true,
      codeType: defaultUnderlyingCodeType,
      underlyingsSyncMode: keepOnBlur ? 'merge' : 'keep_different',
      removeInvalidTagsOnUnderlyingsChange: true,
    });
  const { selectedUnderlyings, selectedUnderlyingsIds } = useUnderlyingsBasket({ underlyings, tags });
  return (
    <div className="input-group">
      <SearchIcon />
      <TagsPicker<Underlying>
        components={{
          MenuList,
          Option,
          NoOptionsMessage,
          ClearIndicator: p => <ClearIndicator.Default {...p} tabIndex={tabIndex} />,
          MultiValueContainer: p => (
            <MultiValueContainer
              {...p}
              canHighlight={true}
              onTagHighlightToggled={onTagHighlightToggled}
              onTagRemoved={onTagRemoved}
            />
          ),
          ...components,
        }}
        convertPastedLabelsToTags={pastedLabels =>
          getOptionsFromPastedLabels(pastedLabels, selectedUnderlyingsIds, defaultUnderlyingCodeType)
        }
        filterTags={input => search(input, selectedUnderlyingsIds, selectedUnderlyings, defaultUnderlyingCodeType)}
        id={id}
        isMulti
        onBlur={updateUnderlyings}
        onTagsChanged={tags => onTagsChanged(tags, false)}
        placeholder={placeholder}
        tags={tags}
        hasErrors={hasErrors}
        isMenuListNarrow
        tabIndex={tabIndex}
        isFocused={isFocused}
      />

      {!isFakeValidateButtonHidden && (
        <button
          data-e2e="add-underlyings-button"
          className="btn btn-sm btn-light ms-1"
          disabled={newValidTags.length === 0}
        >
          <i className="icon icon-sm">done</i>
        </button>
      )}
    </div>
  );
}
